import { useCallback, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import { alpha } from '@mui/material/styles';
import { ThemeProvider } from './theme/ThemeProvider';
import { MarkdownViewer } from './components/MarkdownViewer';
import LandingPage from './components/landing/LandingPage';
import BrandMark from './components/brand/BrandMark';
import { urlSource } from './sources/urlSource';
import { loadSource, type MarkdownDocument } from './sources/MarkdownSource';
import { MARKDOWN_ACCEPT } from './markdown/validation/markdownFileValidator';
import { useMarkdownFileValidator } from './markdown/validation/useMarkdownFileValidator';
import sampleContent from './content/sample.md?raw';

const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

const SAMPLE_DOCUMENT: MarkdownDocument = {
  content: sampleContent,
  name: 'edge-cache.md',
};

const AppContent = () => {
  const [document_, setDocument] = useState<MarkdownDocument | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);
  const requestId = useRef(0);
  const { validate } = useMarkdownFileValidator({ maxBytes: MAX_UPLOAD_BYTES });

  const clearLocation = useCallback(() => {
    window.history.replaceState(null, '', window.location.pathname);
    window.scrollTo({ top: 0 });
  }, []);

  const viewSample = useCallback(() => {
    requestId.current += 1;
    setDocument(SAMPLE_DOCUMENT);
    setLoading(false);
    setError(null);
    clearLocation();
  }, [clearLocation]);

  const goHome = useCallback(() => {
    requestId.current += 1;
    setDocument(null);
    setLoading(false);
    setError(null);
    clearLocation();
  }, [clearLocation]);

  const load = useCallback(async (source: Parameters<typeof loadSource>[0]) => {
    const currentRequest = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const loaded = await loadSource(source);
      if (currentRequest !== requestId.current) return;
      setDocument(loaded);
      window.scrollTo({ top: 0 });
    } catch (cause) {
      if (currentRequest !== requestId.current) return;
      setError(cause instanceof Error ? cause.message : 'The document could not be loaded.');
    } finally {
      if (currentRequest === requestId.current) setLoading(false);
    }
  }, []);

  // `?src=` lets the viewer be linked directly at a remote document.
  useEffect(() => {
    const src = new URLSearchParams(window.location.search).get('src');
    if (src) void load(urlSource(src));
  }, [load]);

  useEffect(() => {
    document.title = document_ ? `${document_.name} — MarkGlass` : 'MarkGlass — Markdown, ready to read';
  }, [document_]);

  const openFile = useCallback(
    async (file: File | undefined) => {
      if (!file) return;

      const currentRequest = ++requestId.current;
      setLoading(true);
      setError(null);
      try {
        const validation = await validate(file);
        if (currentRequest !== requestId.current) return;
        if (!validation) return;
        if (!validation.valid) {
          setError(validation.summary);
          return;
        }

        const content = validation.content ?? (await file.text());
        if (currentRequest !== requestId.current) return;
        setDocument({ content, name: file.name });
        clearLocation();
      } catch (cause) {
        if (currentRequest !== requestId.current) return;
        setError(cause instanceof Error ? cause.message : 'The document could not be loaded.');
      } finally {
        if (currentRequest === requestId.current) setLoading(false);
      }
    },
    [validate, clearLocation],
  );

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      dragDepth.current = 0;
      setDragging(false);
      openFile(event.dataTransfer.files?.[0]);
    },
    [openFile],
  );

  return (
    <Box
      onDragEnter={(event: React.DragEvent) => {
        event.preventDefault();
        dragDepth.current += 1;
        setDragging(true);
      }}
      onDragOver={(event: React.DragEvent) => event.preventDefault()}
      onDragLeave={() => {
        dragDepth.current -= 1;
        if (dragDepth.current <= 0) setDragging(false);
      }}
      onDrop={onDrop}
      sx={{ position: 'relative', minHeight: '100vh' }}
    >
      {document_ && <Box component="a" href="#main-content" className="skip-link">
        Skip to content
      </Box>}

      {document_ ? (
        <MarkdownViewer
          content={document_.content}
          baseUrl={document_.baseUrl}
          title={document_.name}
          subtitle={document_ === SAMPLE_DOCUMENT ? 'Sample document' : 'Markdown document'}
          fileName={document_.name}
          loading={loading}
          onOpenFile={() => inputRef.current?.click()}
          onGoHome={goHome}
        />
      ) : (
        <LandingPage onOpenFile={() => inputRef.current?.click()} onViewSample={viewSample} />
      )}

      {document_ && <Box
        component="footer"
        sx={(theme) => ({
          position: 'relative',
          // The persistent TOC drawer is fixed-positioned and can otherwise
          // paint over the footer when a short document leaves the drawer's
          // viewport-height panel overlapping the footer on screen.
          zIndex: theme.zIndex.drawer + 1,
          px: { xs: 2, sm: 4, lg: 6 },
          py: { xs: 3, sm: 3.5 },
          borderTop: `1px solid ${theme.palette.divider}`,
          backgroundColor: 'background.default',
        })}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 1.5, sm: 2 }}
          alignItems="center"
          justifyContent="space-between"
          sx={{ maxWidth: 1100, mx: 'auto' }}
        >
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: 'text.secondary' }}>
            <BrandMark size={16} />
            <Typography variant="body2" sx={{ fontWeight: 700, letterSpacing: '-0.01em', color: 'text.primary' }}>
              MarkGlass
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              &mdash; Markdown, ready to read
            </Typography>
          </Stack>

          <Stack direction="row" spacing={{ xs: 2, sm: 3 }} alignItems="center">
            <Typography
              component="a"
              href="mailto:devdigest.net@gmail.com"
              variant="caption"
              color="text.secondary"
              sx={{ textDecoration: 'none', '&:hover': { color: 'primary.main', textDecoration: 'underline' } }}
            >
              Support
            </Typography>
            <Typography variant="caption" color="text.secondary">
              © {new Date().getFullYear()} DevDigest LLC
            </Typography>
          </Stack>
        </Stack>
      </Box>}

      <Box
        component="input"
        ref={inputRef}
        type="file"
        accept={MARKDOWN_ACCEPT}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          openFile(event.target.files?.[0]);
          event.target.value = '';
        }}
        sx={{ display: 'none' }}
      />

      {dragging && (
        <Box
          aria-hidden
          sx={(theme) => ({
            position: 'fixed',
            inset: 0,
            zIndex: theme.zIndex.modal,
            display: 'grid',
            placeItems: 'center',
            gap: 1,
            backgroundColor: alpha(theme.palette.background.default, 0.85),
            backdropFilter: 'blur(6px)',
            border: `2px dashed ${theme.palette.primary.main}`,
          })}
        >
          <Box sx={{ textAlign: 'center' }}>
            <UploadFileRoundedIcon sx={{ fontSize: 56, color: 'primary.main', mb: 1 }} />
            <Typography variant="h6">Drop a Markdown file to open it</Typography>
          </Box>
        </Box>
      )}

      <Snackbar
        open={Boolean(error)}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const App = () => (
  <ThemeProvider>
    <AppContent />
  </ThemeProvider>
);

export default App;
